import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/hr/warnings";

// Fetch HrWarnings
export const fetchHrWarnings = createAsyncThunk("hrWarnings/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await hrmsGetRequest(API_URL);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch HR warnings");
  }
});

// Add HrWarning
export const addHrWarning = createAsyncThunk("hrWarnings/add", async (warningData, { rejectWithValue }) => {
  try {
    const response = await hrmsPostRequest(API_URL, warningData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to add HR warning");
  }
});

// Update HrWarning
export const updateHrWarning = createAsyncThunk("hrWarnings/update", async ({ id, updatedData }, { rejectWithValue }) => {
  try {
    const response = await hrmsUpdateRequest(`${API_URL}/${id}`, updatedData);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to update HR warning");
  }
});

// Delete HrWarning
export const deleteHrWarning = createAsyncThunk("hrWarnings/delete", async (id, { rejectWithValue }) => {
  try {
    await hrmsDeleteRequest(`${API_URL}/${id}`);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to delete HR warning");
  }
});

const hrWarningSlice = createSlice({
  name: "hrWarnings",
  initialState: {
    hrWarnings: [],
    status: "idle",
    error: null,
    deleteStatus: "idle",
  },
  reducers: {
    resetDeleteStatus: (state) => {
      state.deleteStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch HrWarnings
      .addCase(fetchHrWarnings.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchHrWarnings.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.hrWarnings = action.payload;
        state.error = null;
      })
      .addCase(fetchHrWarnings.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Add HrWarning
      .addCase(addHrWarning.pending, (state) => {
        state.status = "loading";
      })
      .addCase(addHrWarning.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.hrWarnings.push(action.payload);
        state.error = null;
      })
      .addCase(addHrWarning.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Update HrWarning
      .addCase(updateHrWarning.pending, (state) => {
        state.status = "loading";
      })
      .addCase(updateHrWarning.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.hrWarnings.findIndex((w) => w.id === action.payload.id);
        if (index !== -1) {
          state.hrWarnings[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateHrWarning.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
      })
      // Delete HrWarning
      .addCase(deleteHrWarning.pending, (state) => {
        state.deleteStatus = "loading";
      })
      .addCase(deleteHrWarning.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        state.hrWarnings = state.hrWarnings.filter((w) => w.id !== action.payload);
        state.error = null;
      })
      .addCase(deleteHrWarning.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.payload;
      });
  },
});

export const { resetDeleteStatus: resetHrWarningDeleteStatus } = hrWarningSlice.actions;
export default hrWarningSlice.reducer;